"use client";

import { useEffect, useState } from "react";
import Image from "next/image";

export type GalleryItem = { type: "image" | "video"; src: string; alt: string };

export default function PlaceGallery({ items }: { items: GalleryItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenIndex(null);
      if (e.key === "ArrowRight") setOpenIndex((i) => (i === null ? i : (i + 1) % items.length));
      if (e.key === "ArrowLeft") setOpenIndex((i) => (i === null ? i : (i - 1 + items.length) % items.length));
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIndex, items.length]);

  if (!items.length) {
    return (
      <div className="flex aspect-[4/3] items-center justify-center rounded-2xl bg-slate-100 text-5xl dark:bg-slate-800">
        🗺️
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setOpenIndex(0)}
          className="relative block aspect-[4/3] w-full cursor-zoom-in overflow-hidden rounded-2xl bg-slate-100 shadow-xl dark:bg-slate-800"
          aria-label={`Open ${items[0].type} in full screen`}
        >
          {items[0].type === "video" ? (
            <>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video src={items[0].src} className="h-full w-full object-cover" muted playsInline />
              <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-4xl text-white">▶</span>
            </>
          ) : (
            <Image src={items[0].src} alt={items[0].alt} fill className="object-cover" unoptimized priority sizes="(min-width: 1024px) 420px, 100vw" />
          )}
        </button>

        {items.length > 1 ? (
          <div className="grid grid-cols-3 gap-2">
            {items.slice(1, 4).map((item, idx) => (
              <button
                key={item.src + idx}
                type="button"
                onClick={() => setOpenIndex(idx + 1)}
                className="relative block aspect-square cursor-zoom-in overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800"
                aria-label={`Open ${item.type} in full screen`}
              >
                {item.type === "video" ? (
                  <>
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video src={item.src} className="h-full w-full object-cover" muted playsInline />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-xl text-white">▶</span>
                  </>
                ) : (
                  <Image src={item.src} alt={item.alt} fill className="object-cover" unoptimized sizes="140px" />
                )}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {openIndex !== null ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setOpenIndex(null)}
        >
          <button
            type="button"
            onClick={() => setOpenIndex(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xl text-white hover:bg-white/20"
            aria-label="Close"
          >
            ✕
          </button>

          {items.length > 1 ? (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenIndex((i) => (i === null ? i : (i - 1 + items.length) % items.length));
                }}
                className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-xl text-white hover:bg-white/20"
                aria-label="Previous"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenIndex((i) => (i === null ? i : (i + 1) % items.length));
                }}
                className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-xl text-white hover:bg-white/20"
                aria-label="Next"
              >
                ›
              </button>
            </>
          ) : null}

          <div className="relative max-h-[90vh] w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            {items[openIndex].type === "video" ? (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video src={items[openIndex].src} controls autoPlay className="max-h-[90vh] w-full rounded-lg" />
            ) : (
              <div className="relative h-[90vh] w-full">
                <Image src={items[openIndex].src} alt={items[openIndex].alt} fill className="object-contain" unoptimized sizes="100vw" />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
